import React, { useEffect, useState } from 'react';
import { matchRoutes } from 'react-router-config';
import { Route } from 'react-router';
import RouteDataLoaderContext from './helpers/RouteDataLoaderContext';
import { useLastLocation } from 'react-router-last-location';
import { useLocation } from 'react-router-dom';

/**
 * Fetches any route preload data while changing to that route on the client-side
 * @see /src/server/ssr.js for server-side implementation
 */
const RouteDataLoader = ({ children, routes, store }) => {
    const prevRouterLocation = useLastLocation();
    const currRouterLocation = useLocation();
    const initialRender = prevRouterLocation === null;
    const [loadingData, setLoadingData] = useState(false);
    const [preloadingChunks, setPreloadingChunks] = useState(null);

    // Fetch preload data for current route
    useEffect(() => {
        const buildUrlFromLocation = ({ pathname, search, hash }) => {
            return `${pathname}${search || ''}${hash || ''}`;
        };

        const currLocation = buildUrlFromLocation(currRouterLocation);
        const prevLocation = prevRouterLocation && buildUrlFromLocation(prevRouterLocation);

        if (currLocation !== prevLocation && !initialRender) {
            // Set loading state to pass down to route components
            setLoadingData(true);
            setPreloadingChunks(true);

            // Get all routes that match current location
            const branch = matchRoutes(routes, currRouterLocation.pathname);

            // Preload route chunks for next page
            const preloadPageChunks = branch.map(({ route }) => {
                return Promise.resolve(route.component.load());
            });

            // Resolve chunk preloading promises
            Promise.all(preloadPageChunks)
                .then(() => setPreloadingChunks(false))
                .catch(err => {
                    throw new Error(err);
                });

            // Run each matched route's loadData function if it exists
            const preloadPageData = branch.map(({ route, match }) => {
                if (route.loadData) {
                    return Promise.resolve(route.loadData({ location: currRouterLocation, match, store }));
                }
                return Promise.resolve(null);
            });

            // Resolve route preload fetching promises
            Promise.all(preloadPageData)
                .then(() => setLoadingData(false))
                .catch(err => {
                    throw new Error(err);
                });
        }
    }, [routes, store, currRouterLocation, prevRouterLocation, initialRender]);

    /**
     * Calculate render location
     * Show the previous route while the next page's chunks are loading
     */
    const renderLocation =
        (preloadingChunks === true || preloadingChunks === null) && !initialRender
            ? prevRouterLocation
            : currRouterLocation;

    return (
        <RouteDataLoaderContext.Provider value={loadingData}>
            <Route location={renderLocation} render={() => children} />
        </RouteDataLoaderContext.Provider>
    );
};

export default RouteDataLoader;
